import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  Typography
} from '@mui/material';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { getDoc } from 'firebase/firestore';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { auth } from '../../api/firebase/config';
import {
  addAgentCompany,
  addClientCompany
} from '../../api/firebase/firestore/company';
import {
  addAgentUser,
  addClientUser
} from '../../api/firebase/firestore/user/index';
import { clientRegisterSchema } from '../../api/firebase/firestore/user/validation';
import { sendMailToAgent, sendMailToClient } from '../../api/sendgrid/index';
import {
  blackText,
  cardBorder,
  primaryButtonBackground
} from '../../constants/Colors';
import {
  buttonFont,
  cardContentPadding,
  cardSize,
  textFontSize,
  textMb,
  titleFontSize,
  titleMb
} from '../../constants/Location';
import { agentSteps, clientSteps } from '../../constants/RegisterStep';
import { agentIndustry, useIndustry } from '../../constants/Select';
import { screenType, select } from '../../constants/enum';
import { throwError } from '../../modules/error';
import { sendSlackRegisterAll } from '../../modules/slack';

import CustomLoading from './CustomLoading';
import CustomSelectField from './CustomSelectField';
import CustomSnackbar from './CustomSnackbar';
import CustomStepper from './CustomStepper';
import CustomTextfield from './CustomTextfield';
import PasswordTextFiled from './PasswordTextField';

import type {
  DocumentData,
  DocumentReference,
  DocumentSnapshot
} from 'firebase/firestore';

interface ClientRegisterBasicType {
  email: string;
  password: string;
  companyName: string;
  companyUrl: string;
  industry: number | undefined;
  industryOther: string | null;
  representativeName: string;
  department: string;
}

const styles = {
  buttonFont,
  cardBorder,
  cardContentPadding,
  cardSize,
  textFontSize,
  textMb,
  title: {
    color: blackText,
    fontSize: titleFontSize
  },
  titleMb
};

interface Props {
  screen: number;
}

const RegisterBasicCard: React.FC<Props> = (props): JSX.Element => {
  const { screen } = props;
  const { t } = useTranslation();
  const steps = screen === screenType.client ? clientSteps() : agentSteps();
  const industryList =
    screen === screenType.client ? useIndustry() : agentIndustry();
  const url = new URL(window.location.href);
  const params = url.searchParams;
  const [open, setOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [defaultEmail, setDefaultEmail] = useState<string>('');
  const navigate = useNavigate();

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    formState: { errors }
  } = useForm<ClientRegisterBasicType>({
    resolver: yupResolver(clientRegisterSchema)
  });

  const [firstSubmit, setFirstSubmit] = useState(false);

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        defaultEmail === undefined ||
        '' ||
        watch('password') === undefined ||
        '' ||
        watch('companyName') === undefined ||
        '' ||
        watch('companyUrl') === undefined ||
        '' ||
        watch('industry') === undefined ||
        (watch('industry') === select.other &&
          Boolean(!watch('industryOther'))) ||
        watch('representativeName') === undefined ||
        '' ||
        Boolean(errors.email) ||
        Boolean(errors.password) ||
        Boolean(errors.companyName) ||
        Boolean(errors.companyUrl) ||
        Boolean(errors.industry) ||
        (watch('industry') === select.other && Boolean(errors.industryOther)) ||
        Boolean(errors.representativeName) ||
        Boolean(errors.department)
      );
    }
    return false;
  };

  useEffect(() => {
    setValue('industryOther', null);
  }, [watch('industry')]);

  useEffect(() => {
    try {
      if (params.get('anchor') === 'login') {
        if (!params.get('email')) {
          throw new Error();
        } else {
          setValue('email', params.get('email')?.split('?').join('') ?? '');
          setDefaultEmail(watch('email'));
        }
      } else {
        throw new Error();
      }
    } catch (e) {
      setOpen(true);
      setErrorText(t('registerBasic.linkError'));
      setTimeout(() => {
        navigate('/client/auth/register');
      }, 6000);
      throw new Error();
    }
  }, []);

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      // AuthUserの作成
      const createdUser = await createUserWithEmailAndPassword(
        auth,
        defaultEmail,
        watch('password')
      );

      if (createdUser) {
        // Companiesコレクションにデータを追加
        if (screen === screenType.client) {
          const company: DocumentReference<DocumentData> =
            await addClientCompany(watch);
          // Usersコレクションの更新
          const user = await addClientUser(
            params.get('email'),
            company,
            createdUser,
            watch
          );

          const companySnap = await getDoc(company);
          const userSnap = await getDoc(user);

          if (companySnap.exists() && userSnap.exists()) {
            const industryFind = industryList.find(
              (element): boolean =>
                element.value === (companySnap.data()?.industry as number)
            )?.option;

            const industry: string | undefined =
              select.other === (companySnap.data()?.industry as number)
                ? (companySnap.data()?.industryOther as string)
                : industryFind;
            // Slack通知
            sendSlackRegisterAll(companySnap, userSnap, `${industry ?? ''}`);
            // メール送信
            await sendMailToClient(userSnap, companySnap, `${industry ?? ''}`);
          }
          // 自社情報登録画面へ遷移
          navigate(`/client/auth/company`);
        } else if (screen === screenType.agent) {
          const company: DocumentReference<DocumentData> =
            await addAgentCompany(watch);

          const user = await addAgentUser(
            params.get('email'),
            company,
            createdUser,
            watch
          );

          const userSnap: DocumentSnapshot<DocumentData> = await getDoc(user);
          const companySnap: DocumentSnapshot<DocumentData> = await getDoc(
            company
          );

          if (companySnap.exists() && userSnap.exists()) {
            const industryFind = industryList.find(
              (element): boolean =>
                element.value === (companySnap.data()?.industry as number)
            )?.option;

            const industry: string | undefined =
              select.other === (companySnap.data()?.industry as number)
                ? (companySnap.data()?.industryOther as string)
                : industryFind;

            // Slack通知
            sendSlackRegisterAll(companySnap, userSnap, `${industry ?? ''}`);
            // メール送信
            await sendMailToAgent(userSnap, companySnap, `${industry ?? ''}`);
          }
          navigate(`/agent/auth/company`);
        }
      } else {
        throw new Error();
      }
    } catch (e) {
      throwError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CustomLoading loading={loading} />
      <Grid
        container
        direction="column"
        alignItems="center"
        justifyContent="center"
      >
        <Grid>
          <Card sx={[styles.cardSize, styles.cardBorder]}>
            <CardContent sx={styles.cardContentPadding}>
              <CustomStepper steps={steps} activeStep={0} />
              <Typography sx={[styles.titleMb, styles.title]} component="div">
                {t('registerBasic.step1')}
              </Typography>
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    labelText={t('form.email')}
                    requiredText={t('form.required')}
                    isRequired
                    disabled
                  />
                )}
                control={control}
                name="email"
              />
              <Controller
                render={({ field }) => (
                  <PasswordTextFiled
                    {...field}
                    error={Boolean(errors.password)}
                    helperText={errors.password?.message}
                    labelText={t('form.password')}
                    requiredText={t('form.required')}
                    isRequired
                    placeholder=" "
                    id="password"
                    fullWidth
                  />
                )}
                control={control}
                name="password"
              />
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.companyName)}
                    helperText={errors.companyName?.message}
                    labelText={t('form.companyName')}
                    requiredText={t('form.required')}
                    isRequired
                    placeholder={t('common.enbro')}
                  />
                )}
                control={control}
                name="companyName"
              />
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.companyUrl)}
                    helperText={errors.companyUrl?.message}
                    labelText={t('form.companyUrl')}
                    requiredText={t('form.required')}
                    isRequired
                    placeholder={t('common.companyUrl')}
                    type="url"
                  />
                )}
                control={control}
                name="companyUrl"
              />
              <Controller
                render={({ field }) => (
                  <CustomSelectField
                    {...field}
                    error={Boolean(errors.industry)}
                    helperText={errors.industry?.message}
                    blank
                    labelText={t('form.industry')}
                    requiredText={t('form.required')}
                    isRequired
                    list={industryList}
                    sx={{ width: '400px' }}
                    defaultValue={0}
                    {...(watch('industry') === select.other && {
                      formMb: 'narrow'
                    })}
                  />
                )}
                control={control}
                name="industry"
              />
              {watch('industry') === select.other && (
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={
                        watch('industry') === select.other &&
                        Boolean(errors.industryOther)
                      }
                      helperText={errors.industryOther?.message}
                      isRequired={false}
                      placeholder={t('form.departmentOther')}
                      sx={{ marginTop: '0px' }}
                    />
                  )}
                  control={control}
                  name="industryOther"
                />
              )}
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.representativeName)}
                    helperText={errors.representativeName?.message}
                    labelText={t('form.name')}
                    requiredText={t('form.required')}
                    isRequired
                    placeholder={t('common.name')}
                  />
                )}
                control={control}
                name="representativeName"
              />
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.department)}
                    helperText={errors.department?.message}
                    labelText={t('form.department')}
                    isRequired={false}
                    placeholder={t('common.department')}
                  />
                )}
                control={control}
                name="department"
              />
              <CardActions
                sx={{
                  '&.MuiCardActions-root': {
                    marginTop: '30px'
                  },
                  padding: 0
                }}
              >
                <Grid container direction="column" alignItems="center">
                  <Button
                    disabled={buttonDisabledFlag()}
                    variant="contained"
                    sx={[
                      primaryButtonBackground,
                      {
                        '@media screen and (max-width: 960px)': {
                          height: '42px'
                        },
                        width: '100%'
                      }
                    ]}
                    onClick={() => {
                      void (async () => {
                        setFirstSubmit(true);
                        await handleSubmit(async () => await onSubmit())();
                      })();
                    }}
                  >
                    <Typography
                      component="div"
                      color="white"
                      sx={{ fontSize: 15 }}
                    >
                      {t('registerClient.buttonText')}
                    </Typography>
                  </Button>
                </Grid>
              </CardActions>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <CustomSnackbar
        open={open}
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
        text={errorText}
      />
    </>
  );
};

export default RegisterBasicCard;
